import gsap from 'gsap';
import { CustomEase } from "gsap/CustomEase";

gsap.registerPlugin(CustomEase);

const currentLang = document.documentElement.lang; // en-US or fr-FR

/**
 * Language switcher
 */
function languageSwitcher() {
	const switcher = document.querySelector(".language-switcher");

	if ( ! switcher ) return;

	const items = switcher.querySelectorAll(".lang-item");

	if ( ! items || items.length === 0 ) return;

	// Find which item is currently active (has current-lang class from Polylang)
	const currentItem = switcher.querySelector(".current-lang");

	if (currentItem) {
		const itemsArray = Array.from(items);
		const currentIndex = itemsArray.indexOf(currentItem);

		switcher.classList.add(currentIndex === 0 ? "left" : "right");
	}

	// Add animated class after a short delay to prevent initial load animation
	setTimeout(() => {
		switcher.classList.add('animated');
	}, 100);

	// Click event listener.
	switcher.addEventListener("click", (e) => {
		e.preventDefault();

		// Find the item that is NOT the current one to toggle to it
		let targetItem = null;
		items.forEach(item => {
			if (!item.classList.contains('current-lang')) {
				targetItem = item;
			}
		});

		if (targetItem) {
			const link = targetItem.querySelector('a');
			if (link) {
				// Update UI for immediate feedback
				if (currentItem) currentItem.classList.remove("current-lang");
				targetItem.classList.add("current-lang");

				const itemsArray = Array.from(items);
				const targetIndex = itemsArray.indexOf(targetItem);

				switcher.classList.remove("left", "right");
				switcher.classList.add(targetIndex === 0 ? "left" : "right");

				// Go to the link.
				window.location.href = link.href;
			}
		}
	});
}

/**
 * Quick links
 */
function quickLinks(){
	const quickLinks = document.querySelector('[data-name="menu-quick-links"]');
	const toggle     = document.querySelector('[data-name="quick-links-toggle"]');

	if ( ! quickLinks || ! toggle ) return;

	const arrow = toggle.querySelector('svg');
	const links = quickLinks.querySelectorAll('li');

	if ( quickLinks ) {
		gsap.set( quickLinks, { height: 0, autoAlpha: 0 } );
		if ( links.length > 0 ) {
			gsap.set( links, { y: -15, autoAlpha: 0 } );
		}
	}
	quickLinks.classList.remove('hidden');

	const openQuickLinks = () => {
		quickLinks.classList.add('active');
		
		// Animate container
		gsap.to( quickLinks, { height: 'auto', autoAlpha: 1, duration: 0.6, ease: 'expo.inOut',force3D: true } );

		// Animate parent border radius
		if ( quickLinks.parentElement ) {
			gsap.to( quickLinks.parentElement, { borderRadius: '25px 25px 0 0', duration: 0.4, ease: 'expo.inOut' } );
		}

		// Animate arrow
		if ( arrow ) {
			gsap.to( arrow, { rotation: 180, duration: 0.6, ease: 'expo.inOut',transformOrigin: 'center center' } );
		}	

		// Animate links
		if ( links.length > 0 ) {
			gsap.fromTo( links, { y: -15, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.6, stagger: 0.08, delay: 0.1, ease: 'power3.out',force3D: true } );
		}

		// Animate links
		if ( links.length > 0 ) {
			gsap.fromTo( links, 
				{ y: -15, autoAlpha: 0 }, 
				{ y: 0, autoAlpha: 1, duration: 0.6, stagger: 0.08, delay: 0.1, ease: 'power3.out',force3D: true }
			);
		}
	};

	const closeQuickLinks = () => {
		quickLinks.classList.remove('active');

		// Animate container
		gsap.to( quickLinks, { height: 0, autoAlpha: 0, duration: 0.5, ease: 'expo.inOut', force3D: true } );

		// Animate parent border radius
		if ( quickLinks.parentElement ) {
			gsap.to( quickLinks.parentElement, { borderRadius: '25px', duration: 0.4, ease: 'expo.inOut', delay: 0.1 } );
		}

		// Animate arrow
		if ( arrow ) {
			gsap.to( arrow, { rotation: 0, duration: 0.6, ease: 'expo.inOut', transformOrigin: 'center center' } );
		}

		// Animate links
		if ( links.length > 0 ) {
			gsap.to( links, { y: -15, autoAlpha: 0, duration: 0.3, stagger: 0.03, ease: 'power2.in', force3D: true } );
		}
	};

	// Add event listener
	toggle.addEventListener('click', (e) => {
		e.stopPropagation();
		if ( quickLinks.classList.contains('active') ) {
			closeQuickLinks();
		} else {
			openQuickLinks();
		}
	});

	// Close on click outside
	document.addEventListener('click', (e) => {
		if ( quickLinks.classList.contains('active') ) {
			const isClickInside = quickLinks.contains(e.target) || toggle.contains(e.target);
			if ( ! isClickInside ) {
				closeQuickLinks();
			}
		}
	});
}

/**
 * Main menu
 */
const mainMenuWrapper = document.querySelector('[data-name="main-menu"]');
const mainMenuCurtain = document.querySelector('[data-name="main-menu-curtain"]');
const mainMenuInside  = document.querySelector('[data-name="main-menu"] > div');
const toggle          = document.querySelector('[data-name="menu-toggle"]');
const body            = document.querySelector('body');

const hamburgerCircle = document.querySelector('[data-name="hamburger-circle"]');
const hamburgerLine1  = document.querySelector('[data-name="hamburger-line-1"]');
const hamburgerLine2  = document.querySelector('[data-name="hamburger-line-2"]');
const hamburgerLine3  = document.querySelector('[data-name="hamburger-line-3"]');

const mainMenuContainer            = currentLang === 'en-US' ? document.querySelector('.menu-main-menu-container') : document.querySelector('.menu-menu-principal-container');
const secondaryMenuContainer       = currentLang === 'en-US' ? document.querySelector('.menu-secondary-menu-container') : document.querySelector('.menu-menu-secondaire-container');
const mainMenuPicturesContainer    = document.querySelector('[data-name="main-menu-pictures"]');
const mainMenuLogoContainer        = document.querySelector('[data-name="main-menu-logo"]');
const mainMenuContactContainer     = document.querySelector('[data-name="main-menu-contact"]');
const mainMenuSocialMediaContainer = document.querySelector('[data-name="main-menu-social-media-links"]');

const mainMenuParentLi = mainMenuContainer?.querySelectorAll(':scope > ul > li');
const mainMenuParentA  = mainMenuContainer?.querySelectorAll(':scope > ul > li > a');
const mainMenuChildrenLi = mainMenuContainer?.querySelectorAll('li.menu-item-has-children .sub-menu-wrap-content > ul > li');
const mainMenuChildrenA  = mainMenuContainer?.querySelectorAll('li.menu-item-has-children .sub-menu-wrap-content > ul > li a');
const secondaryMenuLi  = secondaryMenuContainer?.querySelectorAll('li');
const secondaryMenuA   = secondaryMenuContainer?.querySelectorAll('li > a');
const socialMediaItems = mainMenuSocialMediaContainer?.querySelectorAll('a');

const mainMenuPictures = mainMenuPicturesContainer?.querySelectorAll('.main-menu-picture');
const mainMenuContactItems = mainMenuContactContainer?.children;

const mainMenuParentPages     = mainMenuContainer?.querySelectorAll(':scope > ul > li.menu-item-has-children');
const mainMenuParentPagesLink = mainMenuContainer?.querySelectorAll(':scope > ul > li.menu-item-has-children > a');

const siteContent = document.querySelector('#content-wrap');

const disableCSSTransition = targets => gsap.set(targets, { transition: "none" });
const enableCSSTransition  = targets => gsap.set(targets, { clearProps: "transition" });
const customEase        = 'cubic-bezier(0.23, 1, 0.32, 1)';
const customMenuEase    = 'power4.inOut';
const customContentEase = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
const customLinksEase   = CustomEase.create("custom", "M0,0 C0.23,1 0.32,1 1,1");
//const customLinksEase = 'power4.out';
let currentlyShowingPictureId   = null;
let previouslyShowingPictureId  = null;
let closeSubmenusTimeout;
let submenuImageCleanupTimeout;
let mainMenuShowPictureTimeout;
let lastFullyVisiblePicture = null;


function updateBodySubmenuClass() {
	if ( document.querySelector('.sub-menu-wrap.active') ) {
		body.classList.add('main-menu-has-submenu-open');
	} else {
		body.classList.remove('main-menu-has-submenu-open');
	}
}


function mainMenuAnimationStartingPoint(){
	if ( ! mainMenuContainer || ! secondaryMenuContainer || ! mainMenuPicturesContainer || ! mainMenuLogoContainer || ! mainMenuContactContainer || ! mainMenuSocialMediaContainer ) return;
	
	mainMenuParentLi.forEach(li => li.classList.add('overflow-hidden'));
	secondaryMenuLi.forEach(li => li.classList.add('overflow-hidden'));

	gsap.set( mainMenuWrapper, { clipPath: 'inset(0% 0% 100% 0%)', autoAlpha: 1 });

	gsap.set( mainMenuParentA, { autoAlpha: 0, y: (i, target) => target.offsetHeight } );
	gsap.set( mainMenuChildrenA, { autoAlpha: 0, y: 0 } );
	gsap.set( secondaryMenuA, { autoAlpha: 0, y: (i, target) => target.offsetHeight } );
	gsap.set( socialMediaItems, { autoAlpha: 0, y: 100 } );

	if ( mainMenuPictures && mainMenuPictures.length > 0 ) {
		gsap.set( mainMenuPictures, { clipPath: 'inset(100% 0% 0% 0%)' } );
		const lastPic = mainMenuPictures[mainMenuPictures.length - 1];
		gsap.set( lastPic, { clipPath: 'inset(0% 0% 0% 0%)' } );
		lastFullyVisiblePicture = lastPic;
		currentlyShowingPictureId = lastPic.dataset.mainMenuPicture;
	}
}

function mainMenuCheckCurrentPage( eager = false ){
	if ( ! mainMenuParentPages || mainMenuParentPages.length === 0 ) return;

	mainMenuParentPages.forEach(page => {
		if ( page.classList.contains('current-menu-item') || page.classList.contains('current-menu-ancestor') ) {
			mainMenuOpenSubmenu( page.querySelector('.sub-menu-wrap'), eager );
		}
	});

    mainMenuCheckCurrentL2Accordion( eager );
}

function mainMenuCheckCurrentL2Accordion( eager = false ){
    const level2Parents = mainMenuContainer?.querySelectorAll('.sub-menu-wrap .sub-menu > li.menu-item-has-children');
    if ( ! level2Parents ) return;

    level2Parents.forEach(li => {
        if ( li.classList.contains('current-menu-item') || li.classList.contains('current-menu-ancestor') ) {
            const subMenu = li.querySelector('.sub-menu-wrap');
            const arrow = li.querySelector('.submenu-accordion-trigger');

            if ( subMenu ) {
                li.classList.add('active-accordion');
                if ( eager ) {
                    gsap.set(subMenu, { height: 'auto', overflow: 'hidden' });
                } else {
                    gsap.to(subMenu, { height: 'auto', duration: 0.5, ease: "expo.inOut", force3D: true });
                }
            }

            if ( arrow ) {
                arrow.classList.add('active');
                if ( eager ) {
                    gsap.set(arrow, { rotation: 180 });
                } else {
                    gsap.to(arrow, { rotation: 180, duration: 0.5, ease: "expo.inOut" });
                }
            }
        }
    });
}

function mainMenuAnimationOpen(){
	clearTimeout(closeSubmenusTimeout);
	gsap.to( mainMenuCurtain, { autoAlpha: 0.8, duration: 0.3, ease: customEase });
	gsap.to( mainMenuWrapper, { autoAlpha: 1, clipPath: 'inset(0% 0% 0% 0%)', duration: 0.7, ease: customMenuEase });

	if ( siteContent ) {
		gsap.to( siteContent, { y: '10vh', duration: 0.85, ease: customContentEase } );
	}

	if ( mainMenuPictures && mainMenuPictures.length > 0 ) {
		gsap.killTweensOf([mainMenuPictures, mainMenuPicturesContainer]);
	}

	// Hamburger animation
	if ( window.innerWidth < 992 ) {
		const openMenu = gsap.timeline({ paused: true })
			.to( toggle, { mixBlendMode: 'normal', duration: 0.01 } )
			.to(hamburgerCircle, { attr: { r: 29 }, backgroundColor: "#EBB700", borderWidth: '1px', borderColor: '#EBB700', duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine1, { x: 0, y: 6, rotation: -45, transformOrigin: "center center", backgroundColor: "#ffffff", duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine2, { opacity: 0, duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine3, { x: 1, y: -8, rotation: 45, transformOrigin: "center center", backgroundColor: "#ffffff", duration: 0.4, ease: customEase }, 0);
		openMenu.play()
	} else {
		const openMenu = gsap.timeline({ paused: true })
			.to( toggle, { mixBlendMode: 'normal', duration: 0.01 } )
			.to(hamburgerCircle, { attr: { r: 29 }, backgroundColor: "transparent", borderWidth: '1px', borderColor: '#0F4DBC', duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine1, { x: 0, y: 8, rotation: -45, transformOrigin: "center center", backgroundColor: "#0F4DBC", duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine2, { opacity: 0, duration: 0.4, ease: customEase }, 0)
			.to(hamburgerLine3, { x: 1, y: -10, rotation: 45, transformOrigin: "center center", backgroundColor: "#0F4DBC", duration: 0.4, ease: customEase }, 0);
		openMenu.play()
	}

	mainMenuCheckCurrentPage(true)

	if ( mainMenuPictures && mainMenuPictures.length > 0 ) {
		gsap.fromTo( mainMenuPictures[mainMenuPictures.length - 1], { scale: 1 }, { scale: 1.1, duration: 2.5, ease: "power2.out", delay: 0 } );
	}

	const combinedMainA = [];
	if (mainMenuParentA && mainMenuParentA.length > 0) {
		combinedMainA.push(...mainMenuParentA);
	}
	if (combinedMainA.length > 0) {
		disableCSSTransition(combinedMainA);
	}

	if (secondaryMenuA && secondaryMenuA.length > 0) {
		disableCSSTransition(secondaryMenuA);
	}
	if (socialMediaItems && socialMediaItems.length > 0) {
		disableCSSTransition(socialMediaItems);
	}

	if ( mainMenuParentA && mainMenuParentA.length > 0 ) {
		gsap.fromTo( mainMenuParentA, { autoAlpha: 1, y: (i, target) => target.offsetHeight }, { autoAlpha: 1, y: 0, duration: 0.6, ease: customLinksEase, delay: 0.48, onComplete: () => enableCSSTransition(mainMenuParentA) } );
	}
	if ( secondaryMenuA && secondaryMenuA.length > 0 ) {
		gsap.fromTo( secondaryMenuA, { autoAlpha: 1, y: (i, target) => target.offsetHeight }, { autoAlpha: 1, y: 0, duration: 0.6, ease: customLinksEase, delay: 0.48, onComplete: () => enableCSSTransition(secondaryMenuA) } );
	}
	if ( socialMediaItems && socialMediaItems.length > 0 ) {
		gsap.to( socialMediaItems, { autoAlpha: 1, y: 0, duration: 0.8, ease: customLinksEase, delay: 0.48, onComplete: () => enableCSSTransition(socialMediaItems) } );
	}
}

function mainMenuAnimationClose(){
	currentlyShowingPictureId  = null;
	previouslyShowingPictureId = null;
	clearTimeout(mainMenuShowPictureTimeout);
	body.classList.remove('main-menu-has-submenu-open');
	gsap.to( mainMenuCurtain, { autoAlpha: 0, duration: 0.3, ease: customEase });
	gsap.to( mainMenuWrapper, { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.85, ease: customMenuEase });

	if ( siteContent ) {
		gsap.to( siteContent, { y: '0', duration: 0.85, ease: customContentEase } );
	}


	 // Hamburger animation
	const closeMenu = gsap.timeline({ paused: true })
		.to( toggle, { mixBlendMode: 'difference', duration: 0.01 } )
		.to(hamburgerCircle, { attr: { r: 30 }, backgroundColor: "#0F4DBC", borderWidth: '0px', borderColor: 'transparent', duration: 0.4, ease: customEase }, 0)
		.to(hamburgerLine1, { x: 0, y: 0, rotation: 0, transformOrigin: "center center", backgroundColor: "white", duration: 0.4, ease: customEase }, 0)
		.to(hamburgerLine2, { opacity: 1, duration: 0.4, ease: customEase }, 0)
		.to(hamburgerLine3, { x: 0, y: 0, rotation: 0, transformOrigin: "center center", backgroundColor: "white", duration: 0.4, ease: customEase }, 0);
	closeMenu.play()

	const combinedMainAForClose = [];
	if (mainMenuParentA && mainMenuParentA.length > 0) combinedMainAForClose.push(...mainMenuParentA);
	if (mainMenuChildrenA && mainMenuChildrenA.length > 0) combinedMainAForClose.push(...mainMenuChildrenA);
	if (combinedMainAForClose.length > 0) {
		if (mainMenuParentA) gsap.set( mainMenuParentA, { autoAlpha: 1, y: (i, target) => target.offsetHeight, delay: 1 } );
		if (mainMenuChildrenA) gsap.set( mainMenuChildrenA, { autoAlpha: 0, y: 0, delay: 1 } );
	}

	if ( secondaryMenuA && secondaryMenuA.length > 0 ) {
		gsap.set( secondaryMenuA, { autoAlpha: 1, y: (i, target) => target.offsetHeight, delay: 1 } );
	}
	if ( socialMediaItems && socialMediaItems.length > 0 ) {
		gsap.set( socialMediaItems, { autoAlpha: 0, y: (i, target) => target.offsetHeight, delay: 1 } );
	}

	clearTimeout(closeSubmenusTimeout);
	closeSubmenusTimeout = setTimeout(() => {
		mainMenuCloseAllSubmenus();
	}, 800);
}

function mainMenuShowPicture( menuItemId, eager = false ) {
	clearTimeout(mainMenuShowPictureTimeout);
	if ( currentlyShowingPictureId === menuItemId && !eager ) return;
	
	currentlyShowingPictureId  = menuItemId;

	const menuImageToShow       = document.querySelector(`[data-main-menu-picture="${menuItemId}"]`);
	const stableBackgroundToKeep = lastFullyVisiblePicture;

	if ( menuImageToShow ) {
		// Stop ALL ongoing animations on ALL pictures immediately
		mainMenuPictures.forEach(pic => gsap.killTweensOf(pic));
		
		// 1. Z-Index Stack
		mainMenuPictures.forEach(picture => {
			if ( picture === menuImageToShow ) {
				picture.style.zIndex = 30;
			} else if ( picture === stableBackgroundToKeep ) {
				picture.style.zIndex = 20;
				// Ensure the stable background is visible
				gsap.set(picture, { clipPath: 'inset(0% 0% 0% 0%)', autoAlpha: 1 });
			} else {
				picture.style.zIndex = 10;
				gsap.set(picture, { clipPath: 'inset(100% 0% 0% 0%)', scale: 1 });
			}
		});

		// 2. Perform the Reveal		
		gsap.fromTo( menuImageToShow, 
			{ clipPath: 'inset(100% 0% 0% 0%)' }, 
			{ 
				clipPath: 'inset(0% 0% 0% 0%)', 
				duration: ( eager ? 0.001 : 0.8 ),
				ease: customEase, 
				force3D: true,
				onComplete: () => {
					// Only now this image is "stable" enough to be a background
					lastFullyVisiblePicture = menuImageToShow;
					
					// Cleanup: Hide the old background now that the new one is solid
					mainMenuPictures.forEach(picture => {
						if ( picture !== menuImageToShow ) {
							gsap.set( picture, { clipPath: 'inset(100% 0% 0% 0%)', scale: 1, zIndex: 10 } );
						}
					});
				}
			} 
		);
		
		gsap.fromTo( menuImageToShow, 
			{ scale: 1 }, 
			{ scale: 1.1, duration: ( eager ? 0.001 : 2.5 ), ease: "power2.out", force3D: true } 
		);
	}
}

function mainMenuOpenSubmenu( submenu, eager = false ){
	submenu.classList.add('active');
	updateBodySubmenuClass();
	submenu.parentElement.classList.add('its-submenu-is-active');

	const submenuItems = submenu.querySelectorAll('.sub-menu > li');
	const submenuLinks = submenu.querySelectorAll('.sub-menu > li > a, .sub-menu > li .submenu-header-flex > a');
	const submenuTriggers = submenu.querySelectorAll('.submenu-accordion-trigger');
	
	// Kill ALL tweens on these elements including the container itself
	gsap.killTweensOf([submenu, ...submenuItems, ...submenuLinks, ...submenuTriggers]);

	if ( eager ) {
		// Instant visibility for current page submenus
		gsap.set( submenu, { autoAlpha: 1 } );
		if ( submenuItems.length > 0 ) {
			gsap.set( submenuItems, { autoAlpha: 1, borderBottomColor: '#EBB700' } );
		}
		if ( submenuLinks.length > 0 ) {
			disableCSSTransition(submenuLinks);
			gsap.set( submenuLinks, { autoAlpha: 1, y: 0 } );
			enableCSSTransition(submenuLinks);
		}
		if ( submenuTriggers.length > 0 ) {
			gsap.set( submenuTriggers, { autoAlpha: 1 } );
		}
	} else {
		// Fade in the container
		if ( window.innerWidth < 992 ) {
			// Mobile: Slide in from the right
			gsap.fromTo( submenu, 
				{ xPercent: 100, autoAlpha: 1 }, 
				{ xPercent: 0, autoAlpha: 1, duration: 0.6, ease: "expo.out" }
			);
		} else {
			// Desktop: Fade in
			gsap.fromTo( submenu, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, ease: "power2.out" } );
		}

		if ( submenuLinks.length > 0 ) {
			disableCSSTransition(submenuLinks);
			gsap.fromTo( submenuLinks, { autoAlpha: 0, y: 0 }, { autoAlpha: 1, y: 0, duration: 0.6, ease: "power2.out", delay: 0.1, onComplete: () => enableCSSTransition(submenuLinks) } );
		}
		if ( submenuItems.length > 0 ) {
			// Ensure items are hidden but get their border animated in
			gsap.fromTo( submenuItems, { autoAlpha: 1, borderBottomColor: 'transparent' }, { borderBottomColor: '#EBB700', duration: 0.4, ease: "power2.out", delay: 0.1 } );
		}
		if ( submenuTriggers.length > 0 ) {
			gsap.fromTo( submenuTriggers, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.4, ease: "power2.out", delay: 0.1 } );
		}
	}

	// Get attribute data-menu-item-id
	const menuItemId = submenu.closest('li')?.dataset?.menuItemId;
	mainMenuShowPicture( menuItemId, eager );
}

function mainMenuCloseAllSubmenus( exceptThisOne = null, animate = true ){
	mainMenuParentPages.forEach(page => {
		const subMenu = page.querySelector('.sub-menu-wrap');

		if ( subMenu && subMenu !== exceptThisOne ) {
			mainMenuCloseSubmenu( subMenu, animate );
		}
	});
}

function mainMenuCloseSubmenu( submenu, animate = true ){
	const submenuItems = submenu.querySelectorAll('.sub-menu > li');
	const submenuLinks = submenu.querySelectorAll('.sub-menu > li > a, .sub-menu > li .submenu-header-flex > a');
	const submenuTriggers = submenu.querySelectorAll('.submenu-accordion-trigger');

	const finalize = () => {
		submenu.classList.remove('active');
		updateBodySubmenuClass();
		submenu.parentElement.classList.remove('its-submenu-is-active');
		if (animate) {
			gsap.set( [submenu, ...submenuItems, ...submenuLinks, ...submenuTriggers], { autoAlpha: 0 } );
			gsap.set( submenuItems, { borderBottomColor: 'transparent' } );
		}
	};

	if ( animate ) {
		if ( window.innerWidth < 992 ) {
			// Mobile: Slide out to the right
			const otherTargets = [...submenuItems, ...submenuLinks, ...submenuTriggers];
			gsap.to( otherTargets, { autoAlpha: 0, duration: 0.3, ease: "power2.inOut" });

			gsap.to( submenu, {
				xPercent: 100,
				duration: 0.6,
				ease: "expo.inOut",
				onComplete: () => {
					finalize();
					// Reset for next time
					gsap.set(submenu, { clearProps: "xPercent,transform" });
				}
			});
		} else {
			const allTargets = [submenu, ...submenuItems, ...submenuLinks, ...submenuTriggers];
			gsap.to( allTargets, { autoAlpha: 0, duration: 0.4, ease: "power2.inOut", onComplete: finalize } );
		}
	} else {
		gsap.killTweensOf([submenu, ...submenuItems, ...submenuLinks, ...submenuTriggers]);
		if ( submenuItems.length > 0 ) {
			gsap.set( submenuItems, { autoAlpha: 1, borderBottomColor: 'transparent' } );
		}
		if ( submenuLinks.length > 0 ) {
			gsap.set( submenuLinks, { autoAlpha: 0 } );
		}
		if ( submenuTriggers.length > 0 ) {
			gsap.set( submenuTriggers, { autoAlpha: 0 } );
		}
		gsap.set( submenu, { autoAlpha: 0 } );
		finalize();
	}
}

function mainMenuParentPagesAction(){
	if ( ! mainMenuParentPagesLink ) return;

	mainMenuParentPagesLink.forEach(page => {
		page.addEventListener('click', (e) => {
			e.preventDefault();
			e.stopPropagation();
			const target  = e.currentTarget;
			const subMenu = target.parentElement.querySelector('.sub-menu-wrap');

			if ( ! subMenu.classList.contains('active') ) {
				mainMenuCloseAllSubmenus( subMenu );
				mainMenuOpenSubmenu( subMenu );
			} else {
				mainMenuCloseSubmenu( subMenu );
			}
		});
	});
}

function mainMenuParentLinksHover() {
	if ( ! mainMenuParentLi ) return;

	mainMenuParentLi.forEach(li => {
		li.addEventListener('pointerenter', (e) => {
			if ( e.pointerType === 'touch' ) return;
			const menuItemId = li.dataset.menuItemId;
			if ( menuItemId ) {
				clearTimeout(mainMenuShowPictureTimeout);
				mainMenuShowPictureTimeout = setTimeout(() => {
					mainMenuShowPicture( menuItemId );
				}, 150);
			}
		});

		li.addEventListener('pointerleave', (e) => {
			if ( e.pointerType === 'touch' ) return;
			clearTimeout(mainMenuShowPictureTimeout);
		});
	});
}

function backButton(){
	const backButtons = document.querySelectorAll('.back-menu');

	backButtons.forEach(button => {
		button.addEventListener('click', () => {
			const subMenu = button.closest('.sub-menu-wrap');
			if ( subMenu ) {
				mainMenuCloseSubmenu( subMenu );
			}
		});
	});
}

function mobileMenuHeight(){
	const block1 = document.querySelector('[data-name="main-menu-block-1"]');
	const block2 = document.querySelector('[data-name="main-menu-block-2"]');
	const subMenusWrap = document.querySelectorAll('.sub-menu-wrap.level-1');

	if ( ! block1 || ! block2 || ! mainMenuWrapper ) return;

	const block2Height = block2.offsetHeight;

	block1.style.height = mainMenuWrapper.offsetHeight - block2Height + 'px';
	block1.classList.add('overflow-y-auto');
	block1.classList.add('custom-scrollbar');
	

	subMenusWrap.forEach(wrap => {
		wrap.style.height = mainMenuWrapper.offsetHeight - block2Height + 'px';
		wrap.classList.add('overflow-y-auto');
		wrap.classList.add('custom-scrollbar');
	});
}

function desktopMenuWidth(){
	const block1 = document.querySelector('[data-name="main-menu-block-1"]');
	const subMenusWrap = document.querySelectorAll('.sub-menu-wrap.level-1');

	if ( ! block1 ) return;

	if ( window.innerWidth >= 992 ) {
		// On desktop, we let CSS handle the anchors (left: 400, right: 0)
        // We only clear any potential mobile overrides
		subMenusWrap.forEach(wrap => {
			wrap.style.width = '';
			wrap.style.left = '';
            wrap.style.right = '';
		});
	} else {
		// Mobile resets handled in the resize listener
	}
}

function closeOtherLevel2Accordions(currentLi, allL2Lis) {
	allL2Lis.forEach(li => {
		if (li === currentLi) return;
		
		const subMenu = li.querySelector('.sub-menu-wrap');
		const trigger = li.querySelector('.submenu-accordion-trigger');
		
		if (subMenu && li.classList.contains('active-accordion')) {
				li.classList.remove('active-accordion');
				gsap.to(subMenu, { height: 0, overflow: 'hidden', duration: 0.5, ease: 'expo.inOut', force3D: true });
				if(trigger) {
                    trigger.classList.remove('active');
                    gsap.to(trigger, { rotation: 0, duration: 0.5, ease: "expo.inOut" });
                }
		}
	});
}

function mainMenuLevel2Accordions() {
	const level2Parents = mainMenuContainer?.querySelectorAll('.sub-menu-wrap .sub-menu > li.menu-item-has-children');

	if (!level2Parents) return;

	level2Parents.forEach(li => {
		let arrow = li.querySelector('.submenu-accordion-trigger');
        let textWrapper = li.querySelector(':scope > a');
        let flexWrap = li.querySelector('.submenu-header-flex');
        const subMenu = li.querySelector('.sub-menu-wrap');

		if (!flexWrap && textWrapper && subMenu) {
            arrow = document.createElement('span');
            arrow.classList.add('submenu-accordion-trigger');
            arrow.innerHTML = `<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`; 
            
            subMenu.classList.add('w-full');

            flexWrap = document.createElement('div');
            flexWrap.classList.add('submenu-header-flex', 'flex', 'justify-between', 'items-center', 'w-full', 'cursor-pointer');
            
            li.insertBefore(flexWrap, textWrapper);
            flexWrap.appendChild(textWrapper);
            flexWrap.appendChild(arrow);

            if (!li.classList.contains('active-accordion')) {
                gsap.set(subMenu, { height: 0, overflow: 'hidden' });
            }

            flexWrap.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                const isOpen = li.classList.contains('active-accordion');

                if (isOpen) {
                    li.classList.remove('active-accordion');
                    arrow.classList.remove('active');
                    gsap.to(arrow, { rotation: 0, duration: 0.5, ease: "expo.inOut" });
                    gsap.to(subMenu, { height: 0, duration: 0.5, ease: "expo.inOut", force3D: true });
                } else {
                    closeOtherLevel2Accordions(li, level2Parents);
                    
                    li.classList.add('active-accordion');
                    arrow.classList.add('active');
                    gsap.to(arrow, { rotation: 180, duration: 0.5, ease: "expo.inOut" });
                    gsap.to(subMenu, { height: 'auto', duration: 0.5, ease: "expo.inOut", force3D: true });
                }
            });
        }
	});
}

function mainMenuAccessibility(){
	// Makes the menu accessible with the keyboard.
	if ( ! mainMenuWrapper || ! toggle ) return;

	let lastFocusedElement = null;
	let lastOpenedSubmenu = null;

	const isMenuOpen = () => body.classList.contains('main-menu-is-open');
	const toggleA11yLabelOpen = 'Open menu';
	const toggleA11yLabelClose = 'Close menu';

	const syncToggleA11y = () => {
		if (!toggle) return;
		const expanded = isMenuOpen();
		toggle.setAttribute('role', 'button');
		toggle.setAttribute('tabindex', '0');
		toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
		if (mainMenuWrapper?.id) {
			toggle.setAttribute('aria-controls', mainMenuWrapper.id);
		} else {
			toggle.setAttribute('aria-controls', '');
		}
		toggle.setAttribute('aria-label', expanded ? toggleA11yLabelClose : toggleA11yLabelOpen);
	};

	const getFocusable = (root = document) => {
		const selectors = [
			'a[href]',
			'button:not([disabled])',
			'input:not([disabled])',
			'select:not([disabled])',
			'textarea:not([disabled])',
			'[tabindex]:not([tabindex="-1"])'
		];
		return Array.from(root.querySelectorAll(selectors.join(',')))
			.filter(el => {
				if (!el) return false;
				if (el.hasAttribute('disabled')) return false;
				if (el.getAttribute('aria-hidden') === 'true') return false;
				if (el.closest('[hidden]')) return false;
				if (el.closest('.sub-menu-wrap') && !el.closest('.sub-menu-wrap')?.classList.contains('active')) {
					return false;
				}
				const style = window.getComputedStyle(el);
				if (style.display === 'none' || style.visibility === 'hidden') return false;
				return true;
			});
	};

	const rememberTabIndex = (el) => {
		if (!el) return;
		if (!el.dataset) return;
		if (el.dataset.prevTabindexSet === '1') return;
		el.dataset.prevTabindexSet = '1';
		el.dataset.prevTabindex = el.getAttribute('tabindex') ?? '';
	};

	const setTabbable = (el, tabbable) => {
		if (!el) return;
		rememberTabIndex(el);
		if (tabbable) {
			if (el.dataset.prevTabindex === '') {
				el.removeAttribute('tabindex');
			} else {
				el.setAttribute('tabindex', el.dataset.prevTabindex);
			}
		} else {
			el.setAttribute('tabindex', '-1');
		}
	};

	const syncTabbables = () => {
		if ( ! mainMenuWrapper ) return;

		syncToggleA11y();

		const focusablesInMenu = Array.from(mainMenuWrapper.querySelectorAll('a, button, input, select, textarea, [tabindex]'));
		const menuOpen = isMenuOpen();
		focusablesInMenu.forEach(el => setTabbable(el, menuOpen));

		const backButtons = Array.from(mainMenuWrapper.querySelectorAll('.back-menu'));
		backButtons.forEach(btn => {
			btn.setAttribute('role', 'button');
			btn.setAttribute('tabindex', menuOpen ? '0' : '-1');
			btn.setAttribute('aria-label', btn.getAttribute('aria-label') || 'Go back');
		});

		const accordionHeaders = Array.from(mainMenuWrapper.querySelectorAll('.submenu-header-flex'));
		accordionHeaders.forEach(header => {
			header.setAttribute('role', 'button');
			header.setAttribute('tabindex', menuOpen ? '0' : '-1');
		});

		const submenus = Array.from(mainMenuWrapper.querySelectorAll('.sub-menu-wrap'));
		submenus.forEach(sub => {
			const active = sub.classList.contains('active');
			const subLinks = Array.from(sub.querySelectorAll('a, button, input, select, textarea, [tabindex]'));
			subLinks.forEach(el => setTabbable(el, menuOpen && active));
		});
	};

	const focusFirstInMenu = () => {
		if (!isMenuOpen()) return;
		const focusables = getFocusable(mainMenuWrapper);
		if (focusables.length > 0) focusables[0].focus();
	};

	const getTrapFocusables = () => {
		if (!isMenuOpen()) return [];
		const menuFocusables = getFocusable(mainMenuWrapper);
		const list = [toggle, ...menuFocusables].filter(Boolean);
		return Array.from(new Set(list));
	};

	const closeMenu = () => {
		if (!isMenuOpen()) return;
		body.classList.remove('main-menu-is-open');
		mainMenuAnimationClose();
		setTimeout(() => {
			syncTabbables();
			if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
				lastFocusedElement.focus();
			} else {
				toggle.focus();
			}
		}, 50);
	};

	const closeLastSubmenu = () => {
		if (!lastOpenedSubmenu) return false;
		if (!lastOpenedSubmenu.classList.contains('active')) return false;
		mainMenuCloseSubmenu(lastOpenedSubmenu);
		setTimeout(() => {
			syncTabbables();
			const parentLink = lastOpenedSubmenu.closest('li')?.querySelector(':scope > a');
			if (parentLink) parentLink.focus();
		}, 50);
		return true;
	};

	toggle.addEventListener('keydown', (e) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			toggle.click();
		}
	});

	toggle.addEventListener('click', () => {
		if (isMenuOpen()) {
			// Store whatever was focused *before* we move focus into the menu.
			// In most cases this will be the toggle itself (good), but if the menu
			// was opened programmatically we still restore reliably.
			lastFocusedElement = document.activeElement;
			syncTabbables();
			setTimeout(() => {
				syncTabbables();
				focusFirstInMenu();
			}, 750);
		} else {
			syncTabbables();
		}
	});

	mainMenuWrapper.addEventListener('keydown', (e) => {
		if (!isMenuOpen()) return;

		if (e.key === 'Escape') {
			e.preventDefault();
			if (!closeLastSubmenu()) {
				closeMenu();
			}
			return;
		}

		if (e.key === 'Tab') {
			const focusables = getTrapFocusables();
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];
			const active = document.activeElement;
			if (e.shiftKey && active === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && active === last) {
				e.preventDefault();
				first.focus();
			}
			return;
		}

		if (e.key === ' ' || e.key === 'Enter') {
			const back = e.target.closest('.back-menu');
			if (back) {
				e.preventDefault();
				back.click();
				return;
			}

			const accordion = e.target.closest('.submenu-header-flex');
			if (accordion) {
				e.preventDefault();
				accordion.click();
				return;
			}

			const link = e.target.closest('a');
			if (link) {
				if (e.key === ' ') {
					e.preventDefault();
					link.click();
				}
			}
		}
	});

	mainMenuWrapper.addEventListener('click', (e) => {
		if (!isMenuOpen()) return;

		const triggerLink = e.target.closest('a');
		if (triggerLink) {
			const maybeSub = triggerLink.nextElementSibling;
			if (maybeSub && maybeSub.classList.contains('sub-menu-wrap')) {
				lastOpenedSubmenu = maybeSub;
				setTimeout(syncTabbables, 50);
			}
		}

		const back = e.target.closest('.back-menu');
		if (back) {
			setTimeout(syncTabbables, 50);
		}
	});

	const observer = new MutationObserver((mutations) => {
		let shouldSync = false;
		mutations.forEach(m => {
			if (m.type === 'attributes' && m.attributeName === 'class') shouldSync = true;
		});
		if (shouldSync) syncTabbables();
	});

	observer.observe(body, { attributes: true, attributeFilter: ['class'] });
	observer.observe(mainMenuWrapper, { attributes: true, subtree: true, attributeFilter: ['class'] });

	syncToggleA11y();
	syncTabbables();
}

function mainMenu(){
	if ( ! mainMenuWrapper || ! toggle ) return;

	mainMenuAnimationStartingPoint()

	toggle.addEventListener('click', () => {
		body.classList.toggle('main-menu-is-open');

		if ( body.classList.contains('main-menu-is-open') ) {
			mainMenuAnimationOpen()
		} else {
			mainMenuAnimationClose()
		}
	});

	mainMenuParentPagesAction()
	mainMenuParentLinksHover()
	// Mobile
	backButton()

	if ( window.innerWidth < 992 ) {
		mobileMenuHeight()
	} else {
        desktopMenuWidth()
    }

    window.addEventListener('resize', () => {
        if ( window.innerWidth < 992 ) {
            mobileMenuHeight();
            // Reset desktop width
            const subMenusWrap = document.querySelectorAll('.sub-menu-wrap.level-1');
            subMenusWrap.forEach(wrap => {
				wrap.style.width = '';
				wrap.style.left = '';
				wrap.style.right = '';
			});
        } else {
            // Reset mobile height
            const block1 = document.querySelector('[data-name="main-menu-block-1"]');
            const subMenusWrap = document.querySelectorAll('.sub-menu-wrap.level-1');
            if(block1) block1.style.height = '';
            subMenusWrap.forEach(wrap => wrap.style.height = '');
        }
    });

	mainMenuLevel2Accordions();
	mainMenuNavLinks();
	mainMenuAccessibility();
	preventLenisScroll();
}

/**
 * Close menu when a navigation link is clicked
 */
function mainMenuNavLinks() {
	if ( ! mainMenuWrapper ) return;

	mainMenuWrapper.addEventListener('click', (e) => {
		const link = e.target.closest('a');
		if ( ! link ) return;

		// On mobile, check if we should close the menu
		if ( body.classList.contains('main-menu-is-open') && window.innerWidth < 992 ) {
			
			// Identify triggers: links that open submenus rather than navigating away
            // 1. Level 1 triggers (the link is followed by a submenu wrap)
            const isLevel1Trigger = link.nextElementSibling && link.nextElementSibling.classList.contains('sub-menu-wrap');
            
            // 2. Level 2 triggers (the link is inside an accordion header)
            const isLevel2Trigger = link.closest('.submenu-header-flex');
            
            // 3. Back buttons (handled by backButton function)
            const isBackButton = link.classList.contains('back-menu') || link.closest('.back-menu');

			if ( isLevel1Trigger || isLevel2Trigger || isBackButton ) {
				// We don't close the main menu if we are just navigating within it
				return;
			}

			// Check for navigation purpose
			const href = link.getAttribute('href');
			if ( ! href || href === '#' || href.startsWith('javascript:') ) {
				return;
			}

			// If we get here, it's a leaf link or navigation link (including anchor links)
			body.classList.remove('main-menu-is-open');
			console.log('closing...');
			
			mainMenuAnimationClose();
		}
	});
}

// Prevent Lenis scroll on main menu.
function preventLenisScroll() {
	const block1 = document.querySelector('[data-name="main-menu-block-1"]');
	const scrollContainer = document.querySelector('.main-menu-scroll-container');
	const subMenusWrap = document.querySelectorAll('.sub-menu-wrap.level-1');

	const preventPropagation = (e) => {
		e.stopPropagation();
	};

	const applyFix = (el) => {
		if (!el) return;
		el.setAttribute('data-lenis-prevent', '');
		el.addEventListener('wheel', preventPropagation, { passive: false });
		el.addEventListener('touchmove', preventPropagation, { passive: false });
	};

	applyFix(block1);
	applyFix(scrollContainer);
	subMenusWrap.forEach(wrap => applyFix(wrap));
}


export default function menus (){
	languageSwitcher()
	quickLinks()
	mainMenu()
}
